import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import type { NullableProperties } from '@otter-ai/ts-toolkit';
import type {
  UserWorkspace,
  WorkspaceAdminStatus,
} from '@/shared/types/common';

export function isWorkspaceOwner(userWorkspace: UserWorkspace) {
  return (
    userWorkspace.workspace !== null &&
    userWorkspace.workspace.owner.id === userWorkspace.id
  );
}

export function isBasicWorkspace(
  workspace: GetWorkspaceResponse['workspace'] | undefined | null,
) {
  return !workspace || workspace.workspace_type === 'basic';
}

export function isBasicWorkspaceMember(
  adminStatus: NullableProperties<WorkspaceAdminStatus> | null,
  workspace: GetWorkspaceResponse['workspace'] | undefined | null,
): boolean {
  if (!isBasicWorkspace(workspace)) {
    return false;
  }

  if (
    adminStatus?.isAdmin ||
    adminStatus?.isSuperuser ||
    adminStatus?.isSuperAdmin
  ) {
    return false;
  }

  return true;
}

/**
 * Migrated from https://github.com/otter-ai/otter-web/blob/31c255cfa0fb25ea7d1beb49eef7e9ab47e841ba/packages/client/src/app/containers/dashboard/account-settings/autojoin-settings/autojoin-settings.component.ts#L268
 */
export function isWorkspaceAutoShareEnabled(
  workspace: GetWorkspaceResponse['workspace'] | undefined | null,
) {
  return (
    (workspace?.settings?.auto_share_to_all_allowed ||
      workspace?.settings?.auto_share_to_domain_allowed ||
      workspace?.settings?.auto_share_to_workspace_allowed) ??
    true
  );
}

/**
 * Migrated from https://github.com/otter-ai/otter-web/blob/31c255cfa0fb25ea7d1beb49eef7e9ab47e841ba/packages/client/src/app/containers/dashboard/account-settings/autojoin-settings/autojoin-settings.component.ts#L308
 */
export function isWorkspaceOaChatEnabled(
  workspace: GetWorkspaceResponse['workspace'] | undefined | null,
) {
  // TODO(OTWEB-9190): Remove casting when API type added settings.allow_oa_chat
  return (
    (workspace?.settings as unknown as { allow_oa_chat: boolean })
      ?.allow_oa_chat ?? false
  );
}

export function isManualShareBlocked(
  workspace: GetWorkspaceResponse['workspace'] | undefined | null,
): boolean {
  return workspace?.settings?.block_manual_share === true;
}

export function getNumWorkspaceMembers(workspace?: {
  seats: number;
  min_seats: number;
  seats_invoiced: number;
}): number {
  return (
    Math.max(
      workspace?.seats ?? 0,
      workspace?.min_seats ?? 0,
      workspace?.seats_invoiced ?? 0,
    ) || 1
  );
}

/**
 * Calculate minimum seats for checkout, considering plan requirements and workspace size.
 * Matches the client implementation in packages/unowned/client/src/app/core/components/checkout/utils/utils.ts
 */
export function getMinSeats({
  isEdu,
  workspace,
  selectedPlan,
}: {
  isEdu: boolean;
  workspace?: {
    min_seats: number;
    seats: number;
  } | null;
  selectedPlan?: {
    min_seats?: number;
  } | null;
}): number {
  if (isEdu || !workspace) {
    return 1;
  }

  const defaultSeats = selectedPlan?.min_seats ?? 0;
  const workspaceMinSeats = Math.max(workspace.min_seats, workspace.seats);

  return Math.max(defaultSeats, workspaceMinSeats);
}

export { WorkspaceRole, type WorkspaceRoleNumeric } from './workspaceRole';
